import { Owner } from 'src/Utils/globalEnums'
import { useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import {
  ListDocumentRequestsRequest,
  DocumentRequest,
} from '@trustero/trustero-api-web/lib/request/request_pb'
import {
  DOCUMENT_REQUEST_STATUS_TO_LABEL,
  REQUEST_USE_CASE_TO_LABEL,
} from 'src/pages/Requests/requests.constants'
import { prepareSearchableData } from 'src/Utils/search/search.helpers'
import { SearchableData } from 'src/Utils/search/search.types'
import { formatISODate } from 'src/Utils/formatDate'
import { useUsers } from 'src/components/async/Users'
import { useDocumentRequests } from 'src/components/async/DocumentRequest'
import { useRequestAssigneeGroupNames } from 'src/components/async/DocumentRequest/useRequestAssigneeGroups'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import { useControlsbyIds } from 'src/components/async/model/control/useControls'
import { usePolicies } from 'src/components/async/policy/usePolicies'
import { useRisks } from 'src/pages/Risks/risks.hooks'
import { useVendors } from 'src/pages/Vendors/vendors.hooks'
import { useAudits } from 'src/components/async/model/audit/useAudit'
import { getDRLFilterRequest } from './requests.helpers'
import { getLinkedTickets } from './requestsWorkflow.utils'
import { useResolveSourceDisplayNames } from './useResolveSourceDisplayNames'

export const useDRLRequest = (): ListDocumentRequestsRequest => {
  const location = useLocation()

  return useMemo(() => getDRLFilterRequest(location.search), [location.search])
}

export const useSearchableRequests = (
  requests: DocumentRequest[],
): SearchableData<DocumentRequest>[] => {
  const { data: userResponse } = useUsers()
  const groupNames = useRequestAssigneeGroupNames()
  const displayNames = useResolveSourceDisplayNames(requests)

  const allControlIds = useMemo(
    () => Array.from(new Set(requests.flatMap((r) => r.getControlIdsList()))),
    [requests],
  )
  // All four hooks are already called by RequestTypeFilterDropdown — SWR cache hits, no new requests.
  const { data: controlsData } = useControlsbyIds(allControlIds)
  const { data: vendorsData } = useVendors()
  const { data: policiesData } = usePolicies()
  const { data: risksData } = useRisks()
  // The grid's Audit column reads the same list.
  const { data: audits } = useAudits()

  // The Assignee column shows the owner's name, falling back to the email.
  const userMap = useMemo(() => {
    const map: Record<string, string> = {}
    userResponse?.getUsersList().forEach((user) => {
      map[user.getId()] = `${user.getName()} ${user.getEmail()}`.trim()
    })
    return map
  }, [userResponse])

  const auditName = useMemo(() => {
    const map: Record<string, string> = {}
    audits?.getItemsList().forEach((a) => {
      map[a.getId()] = a.getName()
    })
    return map
  }, [audits])

  const controlLabel = useMemo(() => {
    const map: Record<string, string> = {}
    controlsData?.getItemsList().forEach((c) => {
      const label = `${c.getModelId()} ${c.getName()}`.trim()
      map[c.getId()] = label
      map[c.getModelId()] = label
    })
    return map
  }, [controlsData])

  const vendorLabel = useMemo(() => {
    const map: Record<string, string> = {}
    vendorsData?.getVendorsList().forEach((v) => {
      map[v.getId()] = v.getName()
    })
    return map
  }, [vendorsData])

  const policyLabel = useMemo(() => {
    const map: Record<string, string> = {}
    policiesData?.getItemsList().forEach((p) => {
      const name = p.getName()
      map[p.getModelId()] = name
      map[p.getId()] = name
    })
    return map
  }, [policiesData])

  const riskLabel = useMemo(() => {
    const map: Record<string, string> = {}
    risksData?.getRisksList().forEach((r) => {
      map[r.getId()] = `${r.getCustomId()} ${r.getName()}`.trim()
    })
    return map
  }, [risksData])

  const searchableRequest = useMemo(() => {
    return prepareSearchableData(
      requests,
      (request: DocumentRequest) => request.getId(),
      (request: DocumentRequest) => {
        const name = request.getName()
        const description = request.getDescription()
        const evidenceCount = request.getEvidenceCount().toString()
        const controlCount = request.getControlsCount().toString()
        const status = DOCUMENT_REQUEST_STATUS_TO_LABEL[request.getStatus()]
        const dueDate = request.getDueDate()
        const dueDateText = dueDate ? formatISODate(dueDate) : ''
        const ownerId = request.getOwnerId()
        const ownerName = userMap[ownerId]
        const ownerGroupName = groupNames[request.getOwnerGroupId()]
        // The Assignee column reads "Unassigned" with neither a person nor a group.
        const unassigned =
          ownerId || request.getOwnerGroupId() ? '' : Owner.UNASSIGNED

        const displayId = request.getDisplayId()
        const sourceDisplay = displayNames.get(request.getSource()) ?? ''

        return [
          name,
          description,
          displayId,
          evidenceCount,
          controlCount,
          REQUEST_USE_CASE_TO_LABEL[request.getUseCaseType()],
          auditName[request.getAuditId() || ''],
          status,
          dueDateText,
          ownerName,
          ownerGroupName,
          unassigned,
          sourceDisplay,
          ...getLinkedTickets(request).flatMap((ticket) => [
            ticket.key,
            ticket.status,
          ]),
          ...request.getControlIdsList().map((id) => controlLabel[id] ?? id),
          ...request.getPolicyModelIdsList().map((id) => policyLabel[id] ?? id),
          ...request.getVendorIdsList().map((id) => vendorLabel[id] ?? id),
          ...request.getRiskIdsList().map((id) => riskLabel[id] ?? id),
        ]
      },
    )
  }, [
    requests,
    userMap,
    groupNames,
    displayNames,
    controlLabel,
    vendorLabel,
    policyLabel,
    riskLabel,
    auditName,
  ])

  return searchableRequest
}

export const useFilteredRequestIds = (): string[] => {
  const [searchParams] = useSearchParams()
  const request = useDRLRequest()
  const { data } = useDocumentRequests({ request })
  const requests = useMemo(() => data?.getItemsList() || [], [data])
  const searchableRequests = useSearchableRequests(requests)
  const { data: searchFiltered } = useApplySearch(searchableRequests)

  const selectedTypes = useMemo(() => {
    const raw = [
      ...searchParams.getAll('use_case_type[]'),
      ...searchParams.getAll('use_case_type'),
    ]
    return new Set(raw.map(Number).filter((n) => !isNaN(n) && n > 0))
  }, [searchParams])

  return useMemo(
    () =>
      (selectedTypes.size > 0
        ? searchFiltered.filter((r) => selectedTypes.has(r.getUseCaseType()))
        : searchFiltered
      ).map((r) => r.getId()),
    [searchFiltered, selectedTypes],
  )
}
